import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Input, Tabs, Spin } from 'antd';
import IconButton from '../components/IconButton/IconButton';
import AccentButtonDark from '../components/AccentButtonDark/AccentButtonDark';
import { CameraIcon, MicrophoneIcon, ArrowUpIcon, ChevronRightIcon, ChevronLeftIcon, PhotographIcon, UploadIcon, PlusCircleIcon, VolumeUpIcon } from '../components/icons';
import SpecDrawer from '../components/SpecDrawer/SpecDrawer';
import AccentButton from '../components/AccentButton/AccentButton';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { InformationCircleIcon as SolidInfoIcon } from '@heroicons/react/solid';
import { sendChatMessage, ChatMessage, ChatEntities, ToolCall, hasDisplayableEntities, textToSpeech, playAudio, speechToText, ImageData } from '../api/chat';
import ChatEntityCards from '../components/ChatEntityCards/ChatEntityCards';
import './Chat.css';

type ChatMode = 'chat' | 'voice';

interface StarterPrompt {
    title: string;
    description: string;
}

interface DisplayMessage {
    id: string;
    role: 'user' | 'model';
    text: string;
    entities?: ChatEntities;
    toolCalls?: ToolCall[];
}

const STARTER_PROMPTS: StarterPrompt[] = [
    { title: 'Find Shows', description: 'What local bands are playing tonight?' },
    { title: 'Add Show', description: 'Add a show for my band' },
    { title: 'Add Band', description: "Let's add a band" },
];

const VoiceCircleBg: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} width="312" height="312" viewBox="0 0 312 312" fill="none" xmlns="http://www.w3.org/2000/svg">
        <g filter="url(#filter0_d_3378_12343)">
            <circle cx="156" cy="116" r="116" fill="black"/>
        </g>
        <g filter="url(#filter1_d_3378_12343)">
            <circle cx="156" cy="116" r="86" fill="black"/>
        </g>
        <defs>
            <filter id="filter0_d_3378_12343" x="0" y="0" width="312" height="312" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="40"/>
                <feGaussianBlur stdDeviation="20"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0.670588 0 0 0 0 0.670588 0 0 0 0 0.670588 0 0 0 0.08 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3378_12343"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3378_12343" result="shape"/>
            </filter>
            <filter id="filter1_d_3378_12343" x="30" y="30" width="252" height="252" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                <feOffset dy="40"/>
                <feGaussianBlur stdDeviation="20"/>
                <feComposite in2="hardAlpha" operator="out"/>
                <feColorMatrix type="matrix" values="0 0 0 0 0.670588 0 0 0 0 0.670588 0 0 0 0 0.670588 0 0 0 0.08 0"/>
                <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_3378_12343"/>
                <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_3378_12343" result="shape"/>
            </filter>
        </defs>
    </svg>
);

const VoiceWavesIcon: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => (
    <svg className={className} style={style} width="59" height="50" viewBox="0 0 59 50" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect x="16.5928" width="8.2963" height="49.7778" rx="4.14815" fill="white"/>
        <rect y="16.5928" width="8.2963" height="16.5926" rx="4.14815" fill="white"/>
        <rect x="33.1851" y="8.2959" width="8.2963" height="33.1852" rx="4.14815" fill="white"/>
        <rect x="49.7778" y="16.5928" width="8.2963" height="16.5926" rx="4.14815" fill="white"/>
    </svg>
);

const Chat: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [mode, setMode] = useState<ChatMode>('chat');
    const [inputValue, setInputValue] = useState('');
    const [showBanner, setShowBanner] = useState(true);
    const [bannerFading, setBannerFading] = useState(false);
    const [touchActive, setTouchActive] = useState(false);
    const [cameraDrawerOpen, setCameraDrawerOpen] = useState(false);
    const [voiceKey, setVoiceKey] = useState(0);
    const [toastMessage, setToastMessage] = useState<string | null>(null);
    const [messages, setMessages] = useState<DisplayMessage[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [speakingMessageId, setSpeakingMessageId] = useState<string | null>(null);
    const [isSpeaking, setIsSpeaking] = useState(false);
    const [isListening, setIsListening] = useState(false);
    const [isTranscribing, setIsTranscribing] = useState(false);
    const [pendingImage, setPendingImage] = useState<ImageData | null>(null);
    const [imagePreview, setImagePreview] = useState<string | null>(null);
    const currentAudioRef = useRef<HTMLAudioElement | null>(null);
    const mediaRecorderRef = useRef<MediaRecorder | null>(null);
    const audioChunksRef = useRef<Blob[]>([]);
    const streamRef = useRef<MediaStream | null>(null);
    const mimeTypeRef = useRef<string>('audio/webm;codecs=opus');
    const wrapperRef = useRef<HTMLDivElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const isDragging = useRef(false);
    const dragStartX = useRef(0);
    const scrollStartX = useRef(0);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Ref to track if prompt from URL has been handled
    const promptHandledRef = useRef(false);

    const dismissBanner = useCallback(() => {
        if (!showBanner || bannerFading) return;
        setBannerFading(true);
        setTimeout(() => {
            setShowBanner(false);
            setBannerFading(false);
        }, 350);
    }, [showBanner, bannerFading]);

    // Build conversation history for API
    const buildConversationHistory = useCallback((): ChatMessage[] => {
        return messages.map(msg => ({
            role: msg.role,
            parts: [{ text: msg.text }]
        }));
    }, [messages]);

    // Send message handler
    const handleSend = useCallback(async (messageText?: string, imageDataOverride?: ImageData | null) => {
        const text = messageText || inputValue.trim();
        const imageToSend = imageDataOverride !== undefined ? imageDataOverride : pendingImage;

        if ((!text && !imageToSend) || isLoading) return;

        // Build display text (show that image is attached)
        const displayText = imageToSend
            ? text || 'Parse shows from this image'
            : text;

        const userMessage: DisplayMessage = {
            id: `user-${Date.now()}`,
            role: 'user',
            text: displayText
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        setPendingImage(null);
        setImagePreview(null);
        setIsLoading(true);
        dismissBanner();

        try {
            const history = buildConversationHistory();
            const messageToSend = text || 'Parse shows from this image and help me add them';
            const response = await sendChatMessage(messageToSend, history, imageToSend || undefined);

            const assistantMessage: DisplayMessage = {
                id: `model-${Date.now()}`,
                role: 'model',
                text: response.text,
                entities: response.entities,
                toolCalls: response.toolCalls
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            const errorMessage = error instanceof Error ? error.message : 'Failed to send message';
            setToastMessage(errorMessage);
            // Remove the user message on error
            setMessages(prev => prev.filter(m => m.id !== userMessage.id));
        } finally {
            setIsLoading(false);
        }
    }, [inputValue, pendingImage, isLoading, buildConversationHistory, dismissBanner]);

    // Handle Enter key press
    const handleKeyPress = useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    }, [handleSend]);

    // Auto-trigger starter prompt from URL query param (Bug 13)
    // e.g., /chat?prompt=add-show will auto-send "Add a show for my band"
    useEffect(() => {
        if (promptHandledRef.current) return;
        const promptParam = searchParams.get('prompt');
        if (promptParam && messages.length === 0) {
            promptHandledRef.current = true;
            // Map prompt param to starter prompt text
            const promptMap: Record<string, string> = {
                'add-show': 'Add a show for my band',
                'add-band': "Let's add a band",
                'find-shows': 'What local bands are playing tonight?',
            };
            const promptText = promptMap[promptParam];
            if (promptText) {
                // Clear the query param and send the message
                setSearchParams({}, { replace: true });
                // Small delay to ensure component is ready
                setTimeout(() => {
                    handleSend(promptText);
                }, 100);
            }
        }
    }, [searchParams, messages.length, setSearchParams, handleSend]);

    // Text-to-speech for a message
    const speakMessage = useCallback(async (messageId: string, text: string) => {
        // If already speaking this message, stop it
        if (speakingMessageId === messageId && currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
            setSpeakingMessageId(null);
            setIsSpeaking(false);
            return;
        }

        // Stop any currently playing audio
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
        }

        setSpeakingMessageId(messageId);
        setIsSpeaking(true);

        try {
            const response = await textToSpeech(text);
            const audio = playAudio(response.audioContent);
            currentAudioRef.current = audio;

            audio.onended = () => {
                setSpeakingMessageId(null);
                setIsSpeaking(false);
                currentAudioRef.current = null;
            };

            audio.onerror = () => {
                setSpeakingMessageId(null);
                setIsSpeaking(false);
                currentAudioRef.current = null;
                setToastMessage('Failed to play audio');
            };
        } catch (error) {
            setSpeakingMessageId(null);
            setIsSpeaking(false);
            const errorMessage = error instanceof Error ? error.message : 'Text-to-speech failed';
            setToastMessage(errorMessage);
        }
    }, [speakingMessageId]);

    // Cleanup microphone and image preview on unmount
    useEffect(() => {
        // Determine best MIME type once
        let mimeType = 'audio/webm;codecs=opus';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = 'audio/webm';
            if (!MediaRecorder.isTypeSupported(mimeType)) {
                mimeType = 'audio/mp4';
                if (!MediaRecorder.isTypeSupported(mimeType)) {
                    mimeType = '';
                }
            }
        }
        mimeTypeRef.current = mimeType;

        return () => {
            if (streamRef.current) {
                streamRef.current.getTracks().forEach(track => track.stop());
                streamRef.current = null;
            }
            // Cleanup image preview URL
            if (imagePreview) {
                URL.revokeObjectURL(imagePreview);
            }
        };
    }, [imagePreview]);

    // Speech-to-text using Google Cloud API
    const startListening = useCallback(async () => {
        // Stop any current audio playback
        if (currentAudioRef.current) {
            currentAudioRef.current.pause();
            currentAudioRef.current = null;
            setSpeakingMessageId(null);
            setIsSpeaking(false);
        }

        try {
            // Use pre-warmed stream or create new one
            let stream = streamRef.current;

            if (!stream || stream.getTracks()[0]?.readyState !== 'live') {
                console.log('Getting fresh microphone stream...');
                stream = await navigator.mediaDevices.getUserMedia({
                    audio: {
                        echoCancellation: true,
                        noiseSuppression: true,
                        autoGainControl: true,
                    }
                });
                streamRef.current = stream;
            }

            const mimeType = mimeTypeRef.current;
            const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
            const recordedMimeType = mediaRecorder.mimeType;

            audioChunksRef.current = [];

            mediaRecorder.ondataavailable = (event) => {
                console.log('Data available, size:', event.data.size);
                if (event.data.size > 0) {
                    audioChunksRef.current.push(event.data);
                }
            };

            mediaRecorder.onerror = (event) => {
                console.error('MediaRecorder error:', event);
                setToastMessage('Recording error');
                setIsListening(false);
            };

            mediaRecorder.onstop = async () => {
                console.log('MediaRecorder stopped, chunks:', audioChunksRef.current.length);

                if (audioChunksRef.current.length === 0) {
                    setIsListening(false);
                    setToastMessage('No audio captured');
                    return;
                }

                setIsListening(false);
                setIsTranscribing(true);

                try {
                    const audioBlob = new Blob(audioChunksRef.current, { type: recordedMimeType });
                    console.log('Audio blob size:', audioBlob.size, 'bytes');

                    if (audioBlob.size < 500) {
                        setToastMessage('Recording too short');
                        setIsTranscribing(false);
                        return;
                    }

                    const reader = new FileReader();
                    reader.onloadend = async () => {
                        const base64Audio = (reader.result as string).split(',')[1];

                        try {
                            const response = await speechToText(base64Audio, recordedMimeType);
                            if (response.transcript?.trim()) {
                                handleSend(response.transcript);
                                // Switch back to text tab after voice transcription (Bug 11)
                                setMode('chat');
                            } else {
                                setToastMessage('No speech detected');
                            }
                        } catch (error) {
                            console.error('Transcription error:', error);
                            setToastMessage(error instanceof Error ? error.message : 'Transcription failed');
                        } finally {
                            setIsTranscribing(false);
                        }
                    };
                    reader.readAsDataURL(audioBlob);
                } catch (error) {
                    console.error('Audio processing error:', error);
                    setToastMessage('Failed to process audio');
                    setIsTranscribing(false);
                }
            };

            mediaRecorderRef.current = mediaRecorder;
            mediaRecorder.start();
            console.log('Recording started instantly');
            setIsListening(true);

        } catch (error) {
            console.error('Microphone access error:', error);
            setToastMessage('Microphone access denied');
        }
    }, [handleSend]);

    const stopListening = useCallback(() => {
        if (mediaRecorderRef.current?.state === 'recording') {
            mediaRecorderRef.current.stop();
        }
    }, []);

    const toggleListening = useCallback(() => {
        if (isListening) {
            stopListening();
        } else if (!isTranscribing) {
            startListening();
        }
    }, [isListening, isTranscribing, startListening, stopListening]);

    const openPhotoPicker = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handlePhotoSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            // Create preview URL
            const previewUrl = URL.createObjectURL(file);
            setImagePreview(previewUrl);

            // Read as base64
            const reader = new FileReader();
            reader.onloadend = () => {
                const base64 = (reader.result as string).split(',')[1];
                const imageData: ImageData = {
                    base64,
                    mimeType: file.type || 'image/jpeg'
                };
                setPendingImage(imageData);
                dismissBanner();

                // Auto-send with a default message
                handleSend('Parse shows from this image and help me add them', imageData);
            };
            reader.onerror = () => {
                setToastMessage('Failed to read image');
                setImagePreview(null);
            };
            reader.readAsDataURL(file);
        }
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, [dismissBanner, handleSend]);


    // Starter prompt click - sends the prompt message directly
    const handlePromptClick = useCallback((prompt: StarterPrompt) => {
        if (isDragging.current) return;
        handleSend(prompt.description);
    }, [handleSend]);

    // Handle entity selection from chat results (continues conversation instead of navigating)
    const handleEntitySelect = useCallback((entityType: string, entityId: string, entityName: string) => {
        // Send a selection message to continue the conversation
        handleSend(`I want to select ${entityName}`);
    }, [handleSend]);

    // Pause animation on hover/touch
    const handleTrackEnter = useCallback(() => {
        setTouchActive(true);
    }, []);

    const handleTrackLeave = useCallback(() => {
        if (!isDragging.current) setTouchActive(false);
    }, []);

    // Mouse drag for desktop scroll
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        isDragging.current = false;
        dragStartX.current = e.clientX;
        scrollStartX.current = wrapper.scrollLeft;

        const handleMouseMove = (ev: MouseEvent) => {
            const dx = ev.clientX - dragStartX.current;
            if (Math.abs(dx) > 3) isDragging.current = true;
            wrapper.scrollLeft = scrollStartX.current - dx;
        };

        const handleMouseUp = () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            // Reset drag flag after a tick so click handlers can check it
            requestAnimationFrame(() => { isDragging.current = false; });
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, []);

    // Build duplicated prompt list for seamless loop
    const duplicatedPrompts = [...STARTER_PROMPTS, ...STARTER_PROMPTS];

    const handleTabChange = (key: string) => {
        setMode(key as ChatMode);
        dismissBanner();
    };

    const handleVoiceClick = useCallback(() => {
        toggleListening();
    }, [toggleListening]);

    return (
        <div className="show-page chat-page">
            {/* Yellow CTA banner */}
            {showBanner && (
                <div
                    className={`chat-cta-banner${bannerFading ? ' chat-cta-banner-fading' : ''}`}
                    onClick={() => navigate('/signup', { state: { accountType: 'musician' } })}
                >
                    <span className="chat-cta-banner-text">Create a musician account to add a show</span>
                    <ChevronRightIcon size="m" color="#000" />
                </div>
            )}


            <Tabs
                activeKey={mode}
                onChange={handleTabChange}
                size="middle"
                className="chat-tabs"
                centered
                items={[
                    {
                        key: 'chat',
                        label: 'Text',
                        children: (
                            <div className="chat-body">
                                {messages.length === 0 ? (
                                    <>
                                        <div className="chat-bg-logo">
                                            <svg width="87" height="86" viewBox="0 0 87 86" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M12 22.4428C12 17.8069 15.7581 14.0488 20.394 14.0488H66.5607C71.1966 14.0488 74.9547 17.8069 74.9547 22.4428V68.6095C74.9547 73.2454 71.1966 77.0035 66.5607 77.0035H20.394C15.7581 77.0035 12 73.2454 12 68.6095V22.4428Z" fill="#131618"/>
                                                <path d="M63.6026 38.7471C62.3924 38.4697 58.8631 37.7405 55.7026 37.8684C51.8145 38.0258 48.3306 38.7724 46.6558 39.2438C45.751 39.5779 44.3331 39.5191 44.3331 39.5191H44.1728C44.1728 39.5191 42.7549 39.5779 41.8501 39.2438C40.1757 38.7721 36.6917 38.0255 32.8033 37.8684C29.6426 37.7405 26.1133 38.4699 24.9033 38.7471C24.6481 38.8056 24.4691 39.0358 24.4691 39.303V41.1375C24.4691 41.3706 24.6082 41.5802 24.8199 41.6668L24.8772 41.6903C25.0396 41.7566 25.153 41.9058 25.1726 42.0831C25.319 43.4066 26.0235 48.9598 27.6645 50.4456C29.5317 52.1357 32.1663 52.1138 33.7084 52.1357C35.2501 52.1572 39.5194 52.9799 42.6477 43.846C42.7047 43.6796 42.75 43.5067 42.7948 43.3365C42.8858 42.9902 43.164 42.2902 43.929 42.231H44.577C45.3421 42.2898 45.62 42.9899 45.7113 43.3365C45.756 43.5067 45.8014 43.6796 45.8584 43.846C48.9869 52.9797 53.2561 52.1573 54.7977 52.1357C56.3401 52.1141 58.9744 52.1357 60.8416 50.4456C62.4826 48.9604 63.1866 43.407 63.3335 42.0831C63.3531 41.9058 63.4664 41.7566 63.6288 41.6903L63.6861 41.6668C63.8982 41.5802 64.037 41.3706 64.037 41.1375V39.303C64.0363 39.0358 63.8571 38.8056 63.6019 38.7471H63.6026ZM28.0948 48.5918C27.9425 48.3353 27.8159 48.0594 27.72 47.767C27.504 47.1094 27.2632 46.2931 27.0775 45.4425L31.4828 39.2472C31.9896 39.199 32.4636 39.1825 32.8601 39.1825C33.5421 39.1825 33.9986 39.2281 34.0127 39.2297L34.0188 39.23C34.2575 39.2481 34.486 39.2688 34.7085 39.291L28.0948 48.5918ZM49.5266 48.5418C49.0664 47.7066 48.6794 46.6706 48.3801 45.574L52.7633 39.4098C53.2789 39.3395 53.8375 39.2763 54.4577 39.229L54.4641 39.2287C54.4782 39.2272 54.9344 39.1815 55.6164 39.1815C55.7889 39.1815 55.977 39.1846 56.1752 39.1921L49.5266 48.5418Z" fill="black"/>
                                                <circle cx="73.9016" cy="15.0984" r="11.5984" fill="#141719" stroke="black" strokeWidth="3"/>
                                                <path d="M75.0545 18.8853V11.8164H73.9134C73.873 12.0857 73.7888 12.3112 73.6609 12.493C73.533 12.6748 73.3748 12.8229 73.1863 12.9373C73.0045 13.045 72.7958 13.1225 72.5602 13.1696C72.3313 13.21 72.0923 13.2268 71.8432 13.2201V14.3006H73.6205V18.8853H75.0545Z" fill="black"/>
                                            </svg>
                                        </div>

                                        <div className="chat-prompts-clip">
                                            <div className="chat-prompts-wrapper" ref={wrapperRef}>
                                                <div
                                                    className={`chat-prompts-track ${touchActive ? 'paused' : ''}`}
                                                    onTouchStart={handleTrackEnter}
                                                    onTouchEnd={handleTrackLeave}
                                                    onMouseEnter={handleTrackEnter}
                                                    onMouseLeave={handleTrackLeave}
                                                    onMouseDown={handleMouseDown}
                                                >
                                                    {duplicatedPrompts.map((prompt, i) => (
                                                        <div
                                                            key={`${prompt.title}-${i}`}
                                                            className="chat-prompt-card-wrap"
                                                            onClick={() => handlePromptClick(prompt)}
                                                        >
                                                            <h4 className="chat-prompt-send-label">Send</h4>
                                                            <div className="chat-prompt-card">
                                                                <h3 className="chat-prompt-card-title">{prompt.title}</h3>
                                                                <p className="chat-prompt-card-desc">{prompt.description}</p>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <div className="chat-messages">
                                        {messages.map((msg) => {
                                            const showEntities = msg.role === 'model' && hasDisplayableEntities(msg.entities);

                                            return (
                                                <div
                                                    key={msg.id}
                                                    className={`chat-message chat-message-${msg.role}${showEntities ? ' chat-message-with-entities' : ''}`}
                                                >
                                                    <div className="chat-message-content">
                                                        {msg.text}
                                                        {msg.role === 'model' && (
                                                            <button
                                                                className={`chat-message-speak-btn${speakingMessageId === msg.id ? ' speaking' : ''}`}
                                                                onClick={() => speakMessage(msg.id, msg.text)}
                                                                aria-label={speakingMessageId === msg.id ? 'Stop speaking' : 'Speak message'}
                                                            >
                                                                <VolumeUpIcon size="s" tone={speakingMessageId === msg.id ? 'yellow' : 'lightGrey'} />
                                                            </button>
                                                        )}
                                                    </div>
                                                    {showEntities && msg.entities && (
                                                        <ChatEntityCards entities={msg.entities} onEntitySelect={handleEntitySelect} />
                                                    )}
                                                </div>
                                            );
                                        })}
                                        {isLoading && (
                                            <div className="chat-message chat-message-model">
                                                <div className="chat-message-content chat-message-loading">
                                                    <Spin size="small" />
                                                </div>
                                            </div>
                                        )}
                                        <div ref={messagesEndRef} />
                                    </div>
                                )}

                                <div className="chat-input-bar">
                                    <IconButton
                                        className="chat-input-camera"
                                        aria-label="Upload show photo"
                                        icon={<CameraIcon size="m" tone="black" />}
                                        onClick={openPhotoPicker}
                                    />

                                    <Input
                                        autoFocus
                                        placeholder="Ask anything"
                                        value={inputValue}
                                        onChange={(e) => setInputValue(e.target.value)}
                                        onKeyPress={handleKeyPress}
                                        disabled={isLoading}
                                        suffix={
                                            isTranscribing ? (
                                                <Spin size="small" />
                                            ) : (
                                                <MicrophoneIcon
                                                    size="m"
                                                    tone={isListening ? 'white' : 'lightGrey'}
                                                    style={{ cursor: 'pointer' }}
                                                    className={isListening ? 'mic-listening' : ''}
                                                    onClick={handleVoiceClick}
                                                />
                                            )
                                        }
                                        className="chat-input-field"
                                    />

                                    <IconButton
                                        className="chat-input-send"
                                        aria-label="Send message"
                                        icon={isLoading ? <Spin size="small" /> : <ArrowUpIcon size="m" tone="black" />}
                                        onClick={() => handleSend()}
                                        disabled={isLoading || !inputValue.trim()}
                                    />

                                    <IconButton
                                        className="chat-input-voice-toggle"
                                        aria-label="Switch to voice chat"
                                        onClick={handleVoiceClick}
                                        icon={<VoiceWavesIcon className="voice-waves-icon-sm" />}
                                    />
                                </div>
                            </div>
                        ),
                    },
                    {
                        key: 'voice',
                        label: 'Voice',
                        children: (
                            <div key={voiceKey} className="voice-tab-content">
                                <div className="voice-body">
                                    <div
                                        className="voice-circle-outer"
                                        onClick={handleVoiceClick}
                                        style={{ cursor: isTranscribing ? 'wait' : 'pointer' }}
                                    >
                                        <VoiceCircleBg className={`voice-circle-bg${isListening ? ' listening' : ''}${isTranscribing ? ' transcribing' : ''}${isSpeaking ? ' speaking' : ''}`} />
                                        <div className={`voice-circle-button${isListening ? ' listening' : ''}${isTranscribing ? ' transcribing' : ''}`}>
                                            {isTranscribing ? (
                                                <Spin size="large" />
                                            ) : (
                                                <VoiceWavesIcon className={`voice-waves-icon${isListening ? ' listening' : ''}${isSpeaking ? ' speaking' : ''}`} />
                                            )}
                                        </div>
                                    </div>

                                    <div className="voice-label">
                                        <span className={`voice-label-dot${isListening ? ' listening' : ''}${isTranscribing ? ' transcribing' : ''}${isSpeaking ? ' speaking' : ''}`} />
                                        <h5 className="voice-label-text">
                                            {isListening ? 'Listening...' : isTranscribing ? 'Transcribing...' : isSpeaking ? 'Speaking...' : 'Tap to Talk'}
                                        </h5>
                                    </div>
                                </div>

                                <div className="voice-back-button-wrapper">
                                    <AccentButtonDark block size="large" shape="round" onClick={() => setMode('chat')} icon={<ChevronLeftIcon size="m" tone="white" />}>
                                        Back to Chat
                                    </AccentButtonDark>
                                </div>
                            </div>
                        ),
                    },
                ]}
            />

            <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                style={{ display: 'none' }}
                onChange={handlePhotoSelected}
            />

            <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />

            <SpecDrawer
                open={cameraDrawerOpen}
                onClose={() => setCameraDrawerOpen(false)}
                placement="bottom"
                size="large"
                closable={false}
                title={null}
            >
                <div className="chat-camera-drawer-body">
                    <div className="chat-camera-drawer-center">
                        <img
                            src="/images/upload-poster-illustration.svg"
                            alt="Upload show poster"
                            className="chat-camera-drawer-illustration"
                        />
                        <div className="chat-camera-drawer-text-lockup">
                            <p className="chat-camera-drawer-text">
                                Add a show by uploading a show&nbsp;poster
                            </p>
                        </div>
                    </div>
                    <div className="chat-camera-drawer-buttons">
                        <Button
                            type="primary"
                            block
                            size="large"
                            shape="round"
                            icon={<CameraIcon size="m" tone="black" />}
                            onClick={() => { openPhotoPicker(); setCameraDrawerOpen(false); }}
                        >
                            Choose Photo
                        </Button>
                        <AccentButtonDark
                            block
                            size="large"
                            shape="round"
                            onClick={() => setCameraDrawerOpen(false)}
                        >
                            Cancel
                        </AccentButtonDark>
                    </div>
                </div>
            </SpecDrawer>
        </div>
    );
};

export default Chat;
